import { useGetDeviceId } from "@/src/common/analytics/getDeviceId";
import { Box, AHText } from "arkh";
import { useTransition, useCallback } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { FormattedMessage } from "react-intl";
import { ActivityIndicator, Platform } from "react-native";
import { getPollingTokenForDeviceCode } from "./getPollingTokenForDeviceCode";
import { GroupDeviceOTPInput } from "./GroupDeviceOTPInput";
import { LoginWithIndividualAccountFooter } from "./LoginWithIndividualAccountFooter";
import { PollingToken } from "./Polling";
import { Image } from "expo-image";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";

export function GroupDeviceOTP({
  onPollToken,
}: {
  onPollToken: (token: PollingToken) => void;
}) {
  return (
    <ErrorBoundary
      fallbackRender={() => (
        <GroupDeviceOTP_ onPollToken={onPollToken} hadError={true} />
      )}
    >
      <GroupDeviceOTP_ onPollToken={onPollToken} hadError={false} />
    </ErrorBoundary>
  );
}

function GroupDeviceOTP_({
  onPollToken,
  hadError,
}: {
  onPollToken: (token: PollingToken) => void;
  hadError: boolean;
}) {
  const [isPending, startTransition] = useTransition();
  const deviceId = useGetDeviceId();
  const handleCodeEntered = useCallback(
    async (deviceCode: string) => {
      if (isPending) return;
      startTransition(async () => {
        const token = await getPollingTokenForDeviceCode(deviceCode, deviceId);
        onPollToken(token);
      });
    },
    [deviceId, isPending, onPollToken]
  );

  const loadingIndicator = false;

  return (
    <KeyboardAwareScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{ flex: 1 }}
    >
      <Box
        style={{ flex: 1, justifyContent: "center", alignItems: "center" }}
        color="surface-primary"
      >
        <Box
          style={{
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
            maxWidth: 700,
            maxHeight: 800,
            borderWidth: 1,
          }}
          space="large"
          cornerRadius="large"
          borderColor="border-primary"
          color="surface-primary"
        >
          <Image
            source={require("./group_device.png")}
            contentFit="contain"
            style={{ width: 100, height: 100 }}
          />
          <AHText type="title" textAlign="center" spaceVertical="medium">
            <FormattedMessage
              defaultMessage="Log in as shared device"
              description="Title for the shared device login screen"
            />
          </AHText>
          <AHText
            type="subtitle"
            color="text-secondary"
            textAlign="center"
            spaceVertical="medium"
            spaceBottom="xlarge"
          >
            <FormattedMessage
              defaultMessage="Enter the code you received from an administrator."
              description="Subtitle for the shared device login screen"
            />
          </AHText>

          <ContentWrapper>
            {loadingIndicator ? (
              <ActivityIndicator />
            ) : (
              <GroupDeviceOTPInput onComplete={handleCodeEntered} />
            )}
          </ContentWrapper>
          {hadError ? (
            <AHText color="negative" spaceTop="medium">
              <FormattedMessage
                defaultMessage="An error occured, make sure you have the correct code"
                description="GroupDeviceOTP error text"
              />
            </AHText>
          ) : null}
          <LoginWithIndividualAccountFooter />
        </Box>
      </Box>
    </KeyboardAwareScrollView>
  );
}

function ContentWrapper(props: { children: React.ReactNode }) {
  return (
    <Box
      style={{
        justifyContent: "center",
        alignItems: "center",
        minHeight: 70,
        minWidth: Platform.OS === "web" ? 460 : undefined,
      }}
    >
      {props.children}
    </Box>
  );
}
